import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Lock, ShieldCheck, Trash2 } from "lucide-react";
import { api } from "@/lib/api";
import { ConfirmDialog } from "@/components/ConfirmDialog";

const fld = "w-full rounded-xl border border-sbg-raspberry/20 bg-white px-4 py-2.5 text-sm text-sbg-burgundy outline-none focus:border-sbg-fuchsia";

export function PasswordSection() {
  const [status, setStatus] = useState(null);
  const [current, setCurrent] = useState("");
  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);
  const [askRemove, setAskRemove] = useState(false);

  useEffect(() => { api.get("/me/password").then((r) => setStatus(r.data)).catch(() => setStatus({ has_password: false, min_length: 10 })); }, []);

  const min = status?.min_length || 10;
  const valid = pw.length >= min && pw === confirm && (!status?.has_password || current);

  const save = async () => {
    setBusy(true);
    try {
      await api.put("/me/password", { password: pw, current_password: status?.has_password ? current : undefined });
      toast.success(status?.has_password ? "Password updated." : "Password set. You can now sign in with it.");
      setStatus((s) => ({ ...s, has_password: true, set_at: new Date().toISOString() }));
      setPw(""); setConfirm(""); setCurrent(""); setOpen(false);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save password."); }
    finally { setBusy(false); }
  };

  const remove = async () => {
    setBusy(true);
    try {
      await api.delete("/me/password");
      toast.success("Password removed. You'll sign in with emailed codes.");
      setStatus((s) => ({ ...s, has_password: false, set_at: null })); setAskRemove(false);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not remove password."); }
    finally { setBusy(false); }
  };

  if (!status) return null;

  return (
    <section className="rounded-3xl bg-white p-6 ring-1 ring-sbg-raspberry/10" data-testid="account-password">
      <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-sbg-raspberry"><Lock className="h-4 w-4" /> Password</div>
      <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-sbg-burgundy/70" data-testid="account-password-status">
          {status.has_password
            ? <><ShieldCheck className="mr-1 inline h-4 w-4 text-emerald-600" />A password is set. You can sign in with it or with an emailed code.</>
            : "No password set. You sign in with emailed codes. Set a password to skip the code next time."}
        </p>
        {!open && (
          <div className="flex gap-2">
            <button data-testid="account-password-open-btn" onClick={() => setOpen(true)} className="rounded-full bg-sbg-burgundy px-4 py-2 text-xs font-bold text-white">
              {status.has_password ? "Change password" : "Set a password"}
            </button>
            {status.has_password && (
              <button data-testid="account-password-remove-btn" onClick={() => setAskRemove(true)} className="inline-flex items-center gap-1 rounded-full bg-white px-4 py-2 text-xs font-bold text-sbg-burgundy ring-1 ring-sbg-raspberry/20"><Trash2 className="h-3.5 w-3.5" /> Remove</button>
            )}
          </div>
        )}
      </div>
      {open && (
        <div className="mt-4 grid gap-3 sm:grid-cols-2" data-testid="account-password-form">
          {status.has_password && (
            <input type="password" data-testid="account-password-current" className={`${fld} sm:col-span-2`} placeholder="Current password" value={current} onChange={(e) => setCurrent(e.target.value)} autoComplete="current-password" />
          )}
          <input type="password" data-testid="account-password-new" className={fld} placeholder={`New password (min ${min} characters)`} value={pw} onChange={(e) => setPw(e.target.value)} autoComplete="new-password" />
          <input type="password" data-testid="account-password-confirm" className={fld} placeholder="Confirm new password" value={confirm} onChange={(e) => setConfirm(e.target.value)} autoComplete="new-password" />
          {pw && confirm && pw !== confirm && <p className="text-xs font-semibold text-red-600 sm:col-span-2" data-testid="account-password-mismatch">Passwords don't match.</p>}
          <div className="flex gap-2 sm:col-span-2">
            <button data-testid="account-password-save-btn" onClick={save} disabled={!valid || busy} className="inline-flex items-center gap-2 rounded-full bg-sbg-fuchsia px-5 py-2.5 text-sm font-bold text-white disabled:opacity-50">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Lock className="h-4 w-4" />} Save password
            </button>
            <button onClick={() => { setOpen(false); setPw(""); setConfirm(""); setCurrent(""); }} className="text-sm font-semibold text-sbg-burgundy/60 hover:underline">Cancel</button>
          </div>
        </div>
      )}
      <ConfirmDialog open={askRemove} onOpenChange={setAskRemove} title="Remove your password?" description="You'll go back to signing in with emailed codes only." confirmLabel="Remove password" onConfirm={remove} />
    </section>
  );
}
